import React, { useState, useEffect, useContext } from "react";
import styles from "../style/pages/PublicLocationPage.module.css";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchPublicLocationDetails } from "../services/publicAPIService";
import { getFullImageUrl, getThumbnailUrl } from "../utils/urlConfig";
import {
    FaBox,
    FaChevronRight,
    FaDownload,
    FaBoxOpen,
    FaSitemap,
    FaBarcode,
    FaInfoCircle
} from "react-icons/fa";
import QRCode from "react-qr-code";
import { AuthContext } from "../context/AuthContext";
import ActiveScanService from "../services/ActiveScanService";

const PublicLocationPage = () => {
    const [location, setLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { containerId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    useEffect(() => {
        const fetchLocation = async () => {
            try {
                const response = await fetchPublicLocationDetails(containerId);
                setLocation(response);
            } catch (error) {
                setError("Container not found or private.");
            } finally {
                setLoading(false);
            }
        };
        fetchLocation();
    }, [containerId]);

    if (loading) {
        return (
            <div className={styles.loadingWrapper}>
                <div className={styles.spinner}></div>
                <p>Fetching container details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorWrapper}>
                <FaBoxOpen size={48} />
                <h2>Container Not Found</h2>
                <p>{error}</p>
            </div>
        );
    }

    const renderBreadcrumbs = () => {
        const crumbs = [];
        let current = location.parentLocation;
        while (current) {
            crumbs.unshift(current.name);
            current = current.parentLocation;
        }

        if (crumbs.length === 0) return null;

        return (
            <div className={styles.breadcrumbs}>
                {crumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                        <span>{crumb}</span>
                        <FaChevronRight className={styles.breadcrumbSeparator} />
                    </React.Fragment>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.container}>

                {/* Main Content Column */}
                <div className={styles.mainContent}>

                    {/* Header Card */}
                    <div className={styles.card}>
                        <div className={styles.imageWrapper}>
                            {location.image ? (
                                <img src={getFullImageUrl(location.image)} alt={location.name} className={styles.locationImage} loading="eager" />
                            ) : (
                                <div className={styles.noImagePlaceholder}>
                                    <FaBoxOpen size={64} color="#CBD5E1" />
                                </div>
                            )}
                        </div>

                        <div className={styles.locationHeaderInfo}>
                            {renderBreadcrumbs()}
                            <h1 className={styles.locationName}>{location.name}</h1>
                            <p className={styles.description}>{location.description || "No description provided."}</p>
                        </div>
                    </div>

                    {/* Sub-Containers Section */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}><FaSitemap /> Sub-Containers</div>
                        </div>
                        <div className={styles.cardContent}>
                            {location.childLocations?.length > 0 ? (
                                <div className={styles.list}>
                                    {location.childLocations.map((child, idx) => (
                                        <Link key={idx} to={`/public/locations/${child.qrToken || child.id}`} className={styles.listItem}>
                                            <div className={styles.listItemIcon}>
                                                {child.image ? (
                                                    <img src={getThumbnailUrl(child.image)} className={styles.listItemThumb} alt={child.name} loading="lazy" />
                                                ) : <FaSitemap />}
                                            </div>
                                            <div className={styles.listItemContent}>
                                                <div className={styles.listItemTitle}>{child.name}</div>
                                                <div className={styles.listItemSubtitle}>ID: {child.qrToken || `#${child.id}`}</div>
                                            </div>
                                            <FaChevronRight color="#CBD5E1" />
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <p className={styles.noData}>No nested containers.</p>
                            )}
                        </div>
                    </div>

                    {/* Assets Section */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}><FaBox /> Contained Assets</div>
                        </div>
                        <div className={styles.cardContent}>
                            {location.assets?.length > 0 ? (
                                <div className={styles.list}>
                                    {location.assets.map((asset, idx) => (
                                        <Link key={idx} to={`/public/assets/${asset.qrToken || asset.id}`} className={styles.listItem}>
                                            <div className={styles.listItemIcon}>
                                                {asset.image ? (
                                                    <img src={getThumbnailUrl(asset.image)} className={styles.listItemThumb} alt={asset.name} loading="lazy" />
                                                ) : <FaBox />}
                                            </div>
                                            <div className={styles.listItemContent}>
                                                <div className={styles.listItemTitle}>{asset.name}</div>
                                                <div className={styles.listItemSubtitle}>ID: {asset.qrToken || `#${asset.id}`} • {asset.manufacturer || "General"}</div>
                                            </div>
                                            <FaChevronRight color="#CBD5E1" />
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <p className={styles.noData}>No assets found in this container.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className={styles.sidebar}>

                    {/* QR Card */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}><FaBarcode /> Container Label</div>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.qrContainer}>
                                {location.qrToken ? (
                                    <div style={{ background: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                                        <QRCode 
                                            value={`${window.location.origin}/scan/${location.qrToken}`} 
                                            size={150} 
                                            style={{ maxWidth: '100%', height: 'auto' }}
                                        />
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginTop: '8px', color: '#64748b' }}>
                                            {location.qrToken}
                                        </div>
                                    </div>
                                ) : location.qrCode ? (
                                    <>
                                        <img
                                            src={getFullImageUrl(location.qrCode)}
                                            alt="QR Code"
                                            className={styles.qrImage}
                                        />
                                        <a href={getFullImageUrl(location.qrCode)} download className={styles.downloadLink}>
                                            <FaDownload /> Download Label
                                        </a>
                                    </>
                                ) : (
                                    <p className={styles.noData}>Label not generated.</p>
                                )}
                                {user && (
                                    <button 
                                      onClick={async () => {
                        const activeScan = await ActiveScanService.getActiveScan();
                        let stateToSave = {
                          firstItemId: location.id,
                          firstItemType: 'location',
                          secondItemId: -1,
                          secondItemType: ''
                        };

                        if (activeScan && activeScan.firstItemId !== undefined && activeScan.firstItemId !== -1) {
                          if (!(activeScan.firstItemType === 'location' && Number(activeScan.firstItemId) === Number(location.id))) {
                             stateToSave = {
                               firstItemId: activeScan.firstItemId,
                               firstItemType: activeScan.firstItemType,
                               secondItemId: location.id,
                               secondItemType: 'location'
                             };
                          }
                        }

                        await ActiveScanService.saveActiveScan(stateToSave);
                        navigate('/admin?page=scanner');
                                      }}
                                      style={{ marginTop: '15px', width: '100%', padding: '10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                                    >
                                      <FaBarcode style={{ marginRight: '8px' }} /> Open in Scanner
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Info Card */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <div className={styles.cardTitle}><FaInfoCircle /> Container Stats</div>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.metaItem} style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>TOTAL ASSETS</div>
                                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{location.assets?.length || 0}</div>
                            </div>
                            <div className={styles.metaItem}>
                                <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>SUB-CONTAINERS</div>
                                <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{location.childLocations?.length || 0}</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PublicLocationPage;
